import * as React from "react";

/**
 * Result returned by useScrollSentinel hook.
 */
export interface UseScrollSentinelResult {
  /** Ref to attach to the scroll sentinel element */
  ref: React.RefObject<HTMLDivElement>;

  /** Whether the scroll sentinel is currently visible in the viewport */
  isVisible: boolean;
}

/**
 * Hook that tracks whether a scroll sentinel element is visible within a scroll container.
 *
 * Uses IntersectionObserver to efficiently detect when the sentinel enters or leaves
 * the viewport. The sentinel should be placed at the bottom of the scrollable content
 * to detect when the user has scrolled to the bottom.
 *
 * @param scrollContainerRef - Ref to the scroll container (used as IntersectionObserver root)
 *
 * ## Usage:
 *
 * ```tsx
 * const { scrollContainerRef } = useSpacer();
 * const { scrollSentinelRef, isVisible } = useScrollSentinel(scrollContainerRef);
 *
 * // isVisible = true when user is at bottom of content
 * // isVisible = false when user has scrolled up
 *
 * // To scroll to bottom:
 * scrollSentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
 * ```
 */
export function useScrollSentinel(
  scrollContainerRef: React.RefObject<HTMLElement>,
): UseScrollSentinelResult {
  const scrollSentinelRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(
    function observeScrollSentinel() {
      const sentinel = scrollSentinelRef.current;
      if (!sentinel) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        {
          root: scrollContainerRef.current,
          // Adds tolerance to the scroll container's detection area
          // to catch when user is almost at bottom
          rootMargin: "0px 0px 4px 0px",
        },
      );

      observer.observe(sentinel);
      return () => observer.disconnect();
    },
    [scrollContainerRef],
  );

  return {
    ref: scrollSentinelRef,
    isVisible,
  };
}
